import { CSSProperties, useState } from 'react';
import { clsx, makeKey, styx } from 'modules/Utils';
import useScreen from 'Hooks/useScreen';
import PageSymphony from 'elements/PageSymphony';
import CardSymphony from 'elements/CardSymphony';
import InputSymphony from 'elements/InputSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import Loader from 'elements/Loader';
import Banner from 'elements/Banner';
import TagSymphony from 'elements/TagSymphony';
import Icon from 'elements/Icon';
import { 
    reconcileBrandInvoices_api, 
    downloadInvoicesCsv_api,
    ReconcileBrandInvoicesParams,
    ReconcileBrandInvoicesResponse,
    InvoiceRecord
} from 'api/adminApi';
import { toastError, toastSuccess } from 'modules/Toast';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import dayjs from 'dayjs';

const InvoiceDownloader = () => {
    const { mobileView } = useScreen();

    // State for reconciliation operation
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false);
    const [reconcileResponse, setReconcileResponse] = useState<ReconcileBrandInvoicesResponse | null>(null);

    // Form states
    const [searchType, setSearchType] = useState<'brand' | 'user' | 'email' | 'brandName'>('brand');
    const [brandId, setBrandId] = useState<string>('');
    const [userId, setUserId] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [brandName, setBrandName] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [minAmount, setMinAmount] = useState<string>('');
    const [maxAmount, setMaxAmount] = useState<string>('');

    // Run reconciliation operation
    const handleRunReconciliation = async () => {
        setIsLoading(true);
        setReconcileResponse(null);

        try {
            const params: ReconcileBrandInvoicesParams = {};

            // Add the appropriate search parameter based on selected type
            if (searchType === 'brand' && brandId.trim()) {
                const parsedBrandId = parseInt(brandId.trim());
                if (!isNaN(parsedBrandId)) {
                    params.brandId = parsedBrandId;
                } else {
                    toastError('Brand ID must be a valid number');
                    setIsLoading(false);
                    return;
                }
            } else if (searchType === 'user' && userId.trim()) {
                const parsedUserId = parseInt(userId.trim());
                if (!isNaN(parsedUserId)) {
                    params.userId = parsedUserId;
                } else {
                    toastError('User ID must be a valid number');
                    setIsLoading(false);
                    return;
                }
            } else if (searchType === 'email' && email.trim()) {
                params.email = email.trim();
            } else if (searchType === 'brandName' && brandName.trim()) {
                params.brandName = brandName.trim();
            } else {
                const fieldName = searchType === 'brand' ? 'Brand ID' : 
                                searchType === 'user' ? 'User ID' : 
                                searchType === 'email' ? 'Email' : 'Brand Name';
                toastError(`Please provide a valid ${fieldName}`);
                setIsLoading(false);
                return;
            }

            // Add optional date parameters
            if (startDate && endDate) {
                params.startDate = startDate;
                params.endDate = endDate;
            } else if (startDate || endDate) {
                toastError('If providing dates, both start and end dates are required');
                setIsLoading(false);
                return;
            }

            // Add optional amount filtering
            if (minAmount.trim()) {
                const parsedMinAmount = parseFloat(minAmount.trim());
                if (!isNaN(parsedMinAmount)) {
                    params.minAmount = parsedMinAmount;
                } else {
                    toastError('Minimum amount must be a valid number');
                    setIsLoading(false);
                    return;
                }
            }

            if (maxAmount.trim()) {
                const parsedMaxAmount = parseFloat(maxAmount.trim());
                if (!isNaN(parsedMaxAmount)) {
                    params.maxAmount = parsedMaxAmount;
                } else {
                    toastError('Maximum amount must be a valid number');
                    setIsLoading(false);
                    return;
                }
            }

            // Validate amount range
            if (params.minAmount !== undefined && params.maxAmount !== undefined && params.minAmount > params.maxAmount) {
                toastError('Minimum amount cannot be greater than maximum amount');
                setIsLoading(false);
                return;
            }

            const response = await reconcileBrandInvoices_api(params);

            if (response) {
                setReconcileResponse(response);
                toastSuccess(response.message || 'Invoice reconciliation completed successfully');
            } else {
                toastError('Failed to reconcile invoices');
            }
        } catch (error) {
            console.error('Error running invoice reconciliation:', error);
            toastError('An error occurred while reconciling invoices');
        } finally {
            setIsLoading(false);
        }
    };

    // Download CSV export
    const handleDownloadCsv = async () => {
        if (!reconcileResponse) return;

        setIsDownloadingCsv(true);

        try {
            const params: ReconcileBrandInvoicesParams = {};

            // Add the appropriate search parameter
            if (searchType === 'brand' && brandId.trim()) {
                params.brandId = parseInt(brandId.trim());
            } else if (searchType === 'user' && userId.trim()) {
                params.userId = parseInt(userId.trim());
            } else if (searchType === 'email' && email.trim()) {
                params.email = email.trim();
            } else if (searchType === 'brandName' && brandName.trim()) {
                params.brandName = brandName.trim();
            }

            // Add optional date parameters
            if (startDate && endDate) {
                params.startDate = startDate;
                params.endDate = endDate;
            }

            // Add amount filtering
            if (minAmount.trim()) {
                params.minAmount = parseFloat(minAmount.trim());
            }
            if (maxAmount.trim()) {
                params.maxAmount = parseFloat(maxAmount.trim());
            }

            await downloadInvoicesCsv_api(params);
            toastSuccess('CSV download started successfully');
        } catch (error) {
            console.error('Error downloading CSV:', error);
            toastError('Failed to download CSV file');
        } finally {
            setIsDownloadingCsv(false);
        }
    };

    // Clear form and results
    const handleClear = () => {
        setBrandId('');
        setUserId('');
        setEmail('');
        setBrandName('');
        setStartDate('');
        setEndDate('');
        setMinAmount('');
        setMaxAmount('');
        setReconcileResponse(null);
    };

    // Render reconciliation form
    const renderReconciliationForm = () => {
        return (
            <CardSymphony
                className="invoiceDownloader"
                title="Invoice Downloader"
                style={sty.reconcileCard}
            >
                <div className="reconcileContainer" style={sty.reconcileContainer}>
                    <div className="reconcileDescription" style={sty.reconcileDescription}>
                        <p style={Sty.text14grey}>
                            Reconcile brand invoices across PayPal and Stripe payment methods. Search by Brand ID, User ID, Brand Name, or email pattern (supports partial matches like "@unitedmasters.com"). 
                            Dates and amount filtering are optional - leave blank to search all records.
                        </p>
                    </div>

                    {/* Search Type Selection */}
                    <div className="searchTypeSelection" style={sty.searchTypeSelection}>
                        <label style={{...Sty.text14bold, marginBottom: 8}}>Search By:</label>
                        <div className="searchTypeButtons" style={sty.searchTypeButtons}>
                            <ButtonSymphony
                                className="brandSearchButton"
                                variant={searchType === 'brand' ? 'primary' : 'outlined'}
                                size="small"
                                onClick={() => setSearchType('brand')}
                            >
                                Brand ID
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="userSearchButton"
                                variant={searchType === 'user' ? 'primary' : 'outlined'}
                                size="small"
                                onClick={() => setSearchType('user')}
                            >
                                User ID
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="emailSearchButton"
                                variant={searchType === 'email' ? 'primary' : 'outlined'}
                                size="small"
                                onClick={() => setSearchType('email')}
                            >
                                Email
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="brandNameSearchButton"
                                variant={searchType === 'brandName' ? 'primary' : 'outlined'}
                                size="small"
                                onClick={() => setSearchType('brandName')}
                            >
                                Brand Name
                            </ButtonSymphony>
                        </div>
                    </div>

                    {/* Search Input */}
                    <div className="searchInputs" style={sty.searchInputs}>
                        {searchType === 'brand' && (
                            <InputSymphony
                                className="brandIdInput"
                                placeholder="Enter Brand ID"
                                value={brandId}
                                onChange={setBrandId}
                                style={sty.searchInput}
                            />
                        )}
                        {searchType === 'user' && (
                            <InputSymphony
                                className="userIdInput"
                                placeholder="Enter User ID"
                                value={userId}
                                onChange={setUserId}
                                style={sty.searchInput}
                            />
                        )}
                        {searchType === 'email' && (
                            <InputSymphony
                                className="emailInput"
                                placeholder="Enter email or partial email (e.g., @unitedmasters.com)"
                                value={email}
                                onChange={setEmail}
                                style={sty.searchInput}
                            />
                        )}
                        {searchType === 'brandName' && (
                            <InputSymphony
                                className="brandNameInput"
                                placeholder="Enter brand name (supports partial matches)"
                                value={brandName}
                                onChange={setBrandName}
                                style={sty.searchInput}
                            />
                        )}
                    </div>

                    {/* Date Range Inputs */}
                    <div className="dateInputs" style={sty.dateInputs}>
                        <div className="dateInputGroup" style={sty.inputGroup}>
                            <label style={{...Sty.text14bold, marginBottom: 8}}>Date Range (Optional):</label>
                            <div className="dateInputsRow" style={sty.inputsRow}>
                                <InputSymphony
                                    className="startDateInput"
                                    placeholder="Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={setStartDate}
                                    style={sty.dateInput}
                                />
                                <InputSymphony
                                    className="endDateInput"
                                    placeholder="End Date"
                                    type="date"
                                    value={endDate}
                                    onChange={setEndDate}
                                    style={sty.dateInput}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Amount Filtering Inputs */}
                    <div className="amountInputs" style={sty.amountInputs}>
                        <div className="amountInputGroup" style={sty.inputGroup}>
                            <label style={{...Sty.text14bold, marginBottom: 8}}>Amount Filtering (Optional):</label>
                            <div className="amountInputsRow" style={sty.inputsRow}>
                                <InputSymphony
                                    className="minAmountInput"
                                    placeholder="Min Amount (e.g., 426)"
                                    type="number"
                                    value={minAmount}
                                    onChange={setMinAmount}
                                    style={sty.amountInput}
                                />
                                <InputSymphony
                                    className="maxAmountInput"
                                    placeholder="Max Amount (e.g., 630)"
                                    type="number"
                                    value={maxAmount}
                                    onChange={setMaxAmount}
                                    style={sty.amountInput}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="reconcileButtons" style={sty.reconcileButtons}>
                        <ButtonSymphony
                            className="runReconcileButton"
                            onClick={handleRunReconciliation}
                            isLoading={isLoading}
                            disabled={isLoading}
                        >
                            <Icon className="searchIcon" icon="search" size={16} style={{marginRight: 8}} />
                            Reconcile Invoices
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="clearButton"
                            variant="outlined"
                            onClick={handleClear}
                            disabled={isLoading}
                        >
                            Clear
                        </ButtonSymphony>
                    </div>
                </div>
            </CardSymphony>
        );
    };

    // Render invoice row
    const renderInvoiceRow = (invoice: InvoiceRecord, index: number) => {
        const isOrphaned = invoice.campaignId === null;
        const statusColor = isOrphaned ? 'red' : 'green';

        return (
            <div
                key={makeKey('invoice', invoice.brandId, invoice.paymentDate, index)}
                className="invoiceRow"
                style={sty.invoiceRow}
            >
                <div className="invoiceBasicInfo" style={sty.invoiceBasicInfo}>
                    <div className="invoiceIdAndBrand" style={sty.invoiceIdAndBrand}>
                        <div className="brandInfo" style={Sty.text16bold}>
                            {invoice.brandName || `Brand #${invoice.brandId}`}
                        </div>
                        {invoice.campaignName && (
                            <div className="campaignName" style={sty.campaignName}>
                                <span style={Sty.text14}>{invoice.campaignName}</span>
                            </div>
                        )}
                    </div>
                    <div className="invoiceTags" style={sty.invoiceTags}>
                        <TagSymphony
                            className="statusTag"
                            title={isOrphaned ? 'Orphaned Campaign' : 'Has Campaign'}
                            color={statusColor as any}
                            size="small"
                        />
                        <TagSymphony
                            className="platformTag"
                            title={invoice.paymentPlatform.toUpperCase()}
                            size="small"
                            color="blue"
                        />
                        {invoice.campaignPlaybook && (
                            <TagSymphony
                                className="playbookTag"
                                title={invoice.campaignPlaybook}
                                size="small"
                                color="purple"
                            />
                        )}
                    </div>
                </div>
                <div className="invoiceMetadata" style={sty.invoiceMetadata}>
                    <div className="metadataItem" style={sty.metadataItem}>
                        <span style={Sty.text12grey}>Amount: </span>
                        <span style={Sty.text12bold}>
                            {invoice.currency} {invoice.totalAmount.toFixed(2)}
                        </span>
                    </div>
                    <div className="metadataItem" style={sty.metadataItem}>
                        <span style={Sty.text12grey}>Date: </span>
                        <span style={Sty.text12}>
                            {dayjs(invoice.paymentDate).format('MMM DD, YYYY')}
                        </span>
                    </div>
                    {invoice.campaignId && (
                        <div className="metadataItem" style={sty.metadataItem}>
                            <span style={Sty.text12grey}>Campaign: </span>
                            <span style={Sty.text12}>#{invoice.campaignId}</span>
                        </div>
                    )}
                    {invoice.invoiceLink && (
                        <div className="metadataItem" style={sty.metadataItem}>
                            <ButtonSymphony
                                className="viewInvoiceButton"
                                variant="outlined"
                                size="small"
                                onClick={() => window.open(invoice.invoiceLink!, '_blank')}
                            >
                                <Icon className="linkIcon" icon="externalLink" size={12} style={{marginRight: 4}} />
                                View Invoice
                            </ButtonSymphony>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render reconciliation results
    const renderReconciliationResults = () => {
        if (!reconcileResponse) {
            return null;
        }

        return (
            <CardSymphony
                className="reconcileResults"
                title="Reconciliation Results"
                style={sty.resultsCard}
            >
                <div className="resultsContainer" style={sty.resultsContainer}>
                    {/* Summary */}
                    <div className="resultsSummary" style={sty.resultsSummary}>
                        <div className="summaryHeader" style={sty.summaryHeader}>
                            <h3 style={{...Sty.text18, fontWeight: 'bold', marginBottom: 12}}>
                                Summary
                            </h3>
                            {reconcileResponse.invoices.length > 0 && (
                                <ButtonSymphony
                                    className="downloadCsvButton"
                                    onClick={handleDownloadCsv}
                                    isLoading={isDownloadingCsv}
                                    disabled={isDownloadingCsv}
                                    size="small"
                                >
                                    <Icon className="downloadIcon" icon="download" size={14} style={{marginRight: 6}} />
                                    Download CSV
                                </ButtonSymphony>
                            )}
                        </div>
                        <div className="summaryGrid" style={sty.summaryGrid}>
                            <div className="summaryItem" style={sty.summaryItem}>
                                <span style={Sty.text14grey}>Search Method:</span>
                                <span style={Sty.text14bold}>
                                    {reconcileResponse.summary.searchMethod.toUpperCase()} ({reconcileResponse.summary.searchValue})
                                </span>
                            </div>
                            {reconcileResponse.summary.totalBrandsProcessed && (
                                <div className="summaryItem" style={sty.summaryItem}>
                                    <span style={Sty.text14grey}>Brands Processed:</span>
                                    <span style={Sty.text14bold}>{reconcileResponse.summary.totalBrandsProcessed}</span>
                                </div>
                            )}
                            <div className="summaryItem" style={sty.summaryItem}>
                                <span style={Sty.text14grey}>Total Invoices:</span>
                                <span style={Sty.text14bold}>{reconcileResponse.summary.totalInvoicesFound}</span>
                            </div>
                            <div className="summaryItem" style={sty.summaryItem}>
                                <span style={Sty.text14grey}>Orphaned Campaigns:</span>
                                <span style={{...Sty.text14bold, color: reconcileResponse.summary.totalOrphanedCampaigns > 0 ? Colors.red : Colors.green}}>
                                    {reconcileResponse.summary.totalOrphanedCampaigns}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Message */}
                    <Banner
                        className="reconcileMessage"
                        title="Reconciliation Complete"
                        description={reconcileResponse.message || 'Invoice reconciliation completed successfully'}
                        color="green"
                    />

                    {/* Invoices List */}
                    {reconcileResponse.invoices.length > 0 && (
                        <div className="invoicesList" style={sty.invoicesList}>
                            <h3 style={{...Sty.text16, fontWeight: 'bold', marginBottom: 12}}>
                                Invoices ({reconcileResponse.invoices.length})
                            </h3>
                            <div className="invoicesContainer" style={sty.invoicesContainer}>
                                {reconcileResponse.invoices.map(renderInvoiceRow)}
                            </div>
                        </div>
                    )}

                    {/* No Results */}
                    {reconcileResponse.invoices.length === 0 && (
                        <Banner
                            className="noInvoicesBanner"
                            title="No Invoices Found"
                            description="No invoices were found for the specified search criteria and date range."
                            color="grey"
                        />
                    )}
                </div>
            </CardSymphony>
        );
    };

    return (
        <PageSymphony className="invoiceDownloaderPage">
            <div className="invoiceDownloaderPageContent" style={sty.pageContent}>
                <div className="invoiceDownloaderHeader" style={sty.header}>
                    <div className="headerContent" style={sty.headerContent}>
                        <div className="headerText" style={sty.headerText}>
                            <h1 style={{...Sty.text32, fontWeight: 'bold'}}>Invoice Downloader</h1>
                            <p style={Sty.text16grey}>
                                Reconcile and download brand invoices across payment platforms
                            </p>
                        </div>
                    </div>
                </div>

                {renderReconciliationForm()}
                {renderReconciliationResults()}
            </div>
        </PageSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    pageContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        maxWidth: 1200,
        margin: '0 auto',
        padding: 24
    },
    header: {
        marginBottom: 16
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24
    },
    headerText: {
        textAlign: 'center',
        flex: 1
    },
    reconcileCard: {
        backgroundColor: Colors.white
    },
    reconcileContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20
    },
    reconcileDescription: {
        padding: 16,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`
    },
    searchTypeSelection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    searchTypeButtons: {
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap'
    },
    searchInputs: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    searchInput: {
        minWidth: 300
    },
    dateInputs: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    inputsRow: {
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap'
    },
    dateInput: {
        flex: 1,
        minWidth: 200
    },
    amountInputs: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    amountInput: {
        flex: 1,
        minWidth: 200
    },
    reconcileButtons: {
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
    },
    resultsCard: {
        backgroundColor: Colors.white
    },
    resultsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24
    },
    resultsSummary: {
        padding: 16,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`
    },
    summaryHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12
    },
    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16
    },
    summaryItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    invoicesList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    invoicesContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxHeight: 600,
        overflowY: 'auto',
        border: `1px solid ${Colors.greyLight}`,
        borderRadius: 8
    },
    invoiceRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 16,
        borderBottom: `1px solid ${Colors.greyLight}`,
        backgroundColor: Colors.white
    },
    invoiceBasicInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flex: 1
    },
    invoiceIdAndBrand: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4
    },
    campaignName: {
        display: 'flex',
        alignItems: 'center',
        gap: 4
    },
    invoiceTags: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap'
    },
    invoiceMetadata: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 300
    },
    metadataItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 4
    }
};

export default InvoiceDownloader;